import { useState, useEffect, useMemo } from "react";
import {
    TablePagination,
    Button,
    Box,
    FormControl,
    InputAdornment,
    OutlinedInput,
    Typography,
} from "@mui/material";

import Table from "../../components/Table";
import ActionsMenu from "../../components/ActionsMenu";
import BranchModal from "./components/BranchModal";
import { BranchColumns } from './components/BranchColumns';
import AddIcon from '@mui/icons-material/Add';
import SearchRoundedIcon from '@mui/icons-material/SearchRounded';

import api from "../../utils/api";


export default function Branches() {
    const columns = useMemo(() => BranchColumns(), []);

    const [search, setSearch] = useState("");
    const [pagination, setPagination] = useState({ page: 1, perPage: 40, total: 0, totalPages: 0 });
    const [activeFilters, setActiveFilters] = useState({});
    const [loading, setLoading] = useState(true);
    const [rows, setRows] = useState([]);
    const [row, setRow] = useState(null);

    const [isModalOpen, setIsModalOpen] = useState(false);

    const fetchRows = async (filters = activeFilters, options = {}) => {
        const { resetPage = false, overridePagination = {} } = options;

        const currentPage = resetPage ? 1 : overridePagination.page ?? pagination.page;
        const currentPerPage = overridePagination.perPage ?? pagination.perPage;

        setLoading(true);

        try {
            const response = await api.get("admin/branches", {
                params: {
                    filter: filters,
                    page: currentPage,
                    perPage: currentPerPage,
                },
            });

            setPagination({
                page: currentPage,
                perPage: currentPerPage,
                total: response.data.meta.pagination.total,
                totalPages: response.data.meta.pagination.totalPages,
            });

            setRows(response.data.data);
        } catch (error) {
            console.error("Fetch failed", error);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteRows = async (rowsToDelete) => {
        try {
            for (const rowId of rowsToDelete) {
                await api.delete(`admin/branches/${rowId}`);
            }
            fetchRows(activeFilters);
        } catch (err) {
            console.log(err)
        }
    };

    const handleOpenManageRow = (selectedRow) => {
        setRow(selectedRow || null);
        setIsModalOpen(true);
    };

    const handleCloseManageRow = (refetch) => {
        setRow(null);
        setIsModalOpen(false);
        if (refetch) {
            fetchRows();
        }
    };

    const handlePageChange = (newPage) => {
        fetchRows(activeFilters, { overridePagination: { page: newPage + 1 } });
    };

    useEffect(() => {
        const delayDebounce = setTimeout(() => {
            const newFilters = { name: search };
            setActiveFilters(newFilters);
            fetchRows(newFilters, { resetPage: true });
        }, 500);
        return () => clearTimeout(delayDebounce);
    }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
        <Box>
            <Typography component="h2" variant="h6" sx={{ mb: 2 }}>
                Branches
            </Typography>
            <Box>
                <Box sx={{ display: "flex", mb: 2, justifyContent: "space-between" }}>
                    <Box>
                        <FormControl sx={{ width: { xs: '100%', md: '45ch' } }} variant="outlined">
                            <OutlinedInput
                                size="small"
                                id="search"
                                placeholder="Search..."
                                sx={{ flexGrow: 1, background: "#fff" }}
                                onChange={(e) => setSearch(e.target.value)}
                                value={search}
                                startAdornment={
                                    <InputAdornment position="start" sx={{ color: 'text.primary' }}>
                                        <SearchRoundedIcon fontSize="small" />
                                    </InputAdornment>
                                }
                                inputProps={{
                                    'aria-label': 'search',
                                }}
                            />
                        </FormControl>
                    </Box>
                    <Box sx={{ display: "flex" }}>
                        <Button variant="contained" size="small" startIcon={<AddIcon color='success' />} onClick={() => handleOpenManageRow()}>
                            Create New
                        </Button>
                    </Box>
                </Box>

                <Table
                    loading={loading}
                    rows={rows}
                    columns={columns}
                    selactable={false}
                    draggable={false}
                    renderActions={(tableRow) => (
                        <>
                            <ActionsMenu
                                actions={[
                                    { label: "Edit", onClick: () => handleOpenManageRow(tableRow) },
                                    {
                                        label: "Delete",
                                        onClick: () => handleDeleteRows([tableRow.id]),
                                        confirm: true,
                                        confirmText: `Are you sure you want to delete this branch?`,
                                    },
                                ]}
                            />
                        </>
                    )} />

                <TablePagination
                    component="div"
                    count={pagination.total}
                    page={pagination.page - 1}
                    rowsPerPage={pagination.perPage}
                    onPageChange={(event, newPage) => {
                        handlePageChange(newPage)
                    }}
                    rowsPerPageOptions={[]}
                />
            </Box>
            <BranchModal
                row={row}
                open={isModalOpen}
                onClose={handleCloseManageRow}
            />
        </Box >
    );
}
